import React, { useCallback } from "react";
import Dialog from "material-ui/Dialog";
import RaisedButton from "material-ui/RaisedButton";
import { Link } from "react-router";
import { CardText } from "material-ui/Card";
import { makeTitle } from "../reducer";
import useResponse from "../hooks/useResponse";
import { LinearProgress } from "material-ui";
import IconButton from 'material-ui/IconButton';
import ActionHome from 'material-ui/svg-icons/image/edit';
import {DropDownMenu, MenuItem} from 'material-ui/DropDownMenu';
import {FormattedMessage, useIntl} from "react-intl";
import {IntlProvider} from 'react-intl';
import { useDispatch } from 'react-redux'
import { updateTrForResponse } from '../actions/translation';
import { SAVE_TR_FOR_RESPONSE } from '../actions/translation';
import SelectField from "material-ui/SelectField";
import {getTranslationMessages} from "../reducer/translations"

require("./FisheryStatusModal.css");

const SectionInfo = props => {
  let results = props.results[props.section];
  const total = results.completedQuestions + results.incompleteQuestions;
  const shouldJustFinish = results.completedQuestions / total > 0.75;
  const intl = useIntl();

  if (results.complete) {
    return (
      <div>
        <div className="status"><FormattedMessage id="fisherystatusmodal.complete" defaultMessage="Complete!" /></div>
        <RaisedButton
          primary={true}
          label={intl.formatMessage({id: 'fisherystatusmodal.view', defaultMessage:"View Results"})}
          containerElement={
            <Link to={`/questionnaire/results/${props.metadata.id}/${props.section}`} />
          }
        />
      </div>
    );
  } else {
    if (results.completedQuestions > 0) {
      return (
        <div>
          <div className="status">
            <FormattedMessage id="fisherystatusmodal.numberAnswered" values={{completed:results.completedQuestions, total:results.completedQuestions + results.incompleteQuestions}}
            defaultMessage="{completed, number} of {total, number} questions answered" />
          </div>
          <RaisedButton
            primary={true}
            label={intl.formatMessage({id:"general.continue", defaultMessage:"Continue"})}
            containerElement={
              <Link
                to={`/questionnaire/${props.metadata.id}/${
                  shouldJustFinish ? "finish/" : ""
                }${props.section}/${results.nextQuestion.id}`}
              />
            }
          />
        </div>
      );
    } else {
      return (
        <div>
          <div className="status"><FormattedMessage id="fisherystatusmodal.notStarted" defaultMessage="Not yet started"/></div>
          <RaisedButton
            primary={true}
            label={intl.formatMessage({id:"general.begin", defaultMessage:"Begin"})}
            containerElement={
              <Link
                to={`/questionnaire/${props.metadata.id}/${props.section}`}
              />
            }
          />
        </div>
      );
    }
  }
};

const FisheryStatusModal = ({ responseId, open, onRequestClose, calculateResults, onRequestEditMetadata, readOnly, translationList, onTrDropDownMenuChange, trID }) => {
  const { response, loading, error } = useResponse(responseId);

  const intl = useIntl();
  let messages = getTranslationMessages(trID)
  if (loading) {
    return (
      <Dialog open={open} title={intl.formatMessage({id:"general.loadingFishery", defaultMessage:"Loading fishery"})}>
        <LinearProgress />
      </Dialog>
    );
  } else if (error) {
    return (
      <Dialog
        open={open}
        title={intl.formatMessage({id:"general.errorRetrievingFishery", defaultMessage:"Error retrieving fishery"})}
        actions={[<RaisedButton label={intl.formatMessage({id:"general.close", defaultMessage:"Close"})} onClick={onRequestClose} />]}
      >
        {error}
      </Dialog>
    );
  } else {
    var results = null;
    if (response) {
      results = calculateResults(response);
    }

    let title = makeTitle(response);
    if (readOnly) { title += " - " + intl.formatMessage({id:"general.viewOnly", defaultMessage:"View Only"}) }
    let introText = <FormattedMessage id="fisherystatusmodal.introTextPartial"
                        defaultMessage="This questionnaire is partially complete. You can review results for completed sections or continue where you left off." />


    if (results) {
      if (
        !results.monitoring.completedQuestions &&
        !results.assessment.completedQuestions &&
        !results.controlRules.completedQuestions
      ) {
        introText = <FormattedMessage id="fisherystatusmodal.introTextNew" defaultMessage="The FishPath questionnaire is divided into 3 sections. You can complete and review results from these sections independently." />
      } else if (
        results.monitoring.complete &&
        results.assessment.complete &&
        results.controlRules.complete
      ) {
        introText = <FormattedMessage id="fisherystatusmodal.introTextComplete" defaultMessage="You've completed all 3 sections of this questionnaire! Access results for each section below." />
      }
    }

    //default to english
    if(!trID){trID = -1}

    return (
      <Dialog
        open={open}
        title={title}
        onRequestClose={onRequestClose}
        titleStyle={{ padding: "24px 24px 0px" }}
        autoScrollBodyContent={true}
        contentStyle={{border: readOnly ? '5px solid orange' : null}}
      >
        {!readOnly && <IconButton style={{position: 'absolute', right: 20, top: 5}} onClick={onRequestEditMetadata} tooltip={intl.formatMessage({id:"fisherystatusmodal.editDetailsTooltip", defaultMessage:"Edit Name and Details"})}>
          <ActionHome />
        </IconButton>}
        <IntlProvider locale="en" defaultLocale="en" messages={messages}> {/*TODO: why is this not being covered by the IntlProvider in routes?*/}
          <CardText style={{ padding: "16px 0px" }}>{introText}</CardText>
          <div className="cardGroup">
            <div
              className={
                results.monitoring.complete ? "card complete" : "card"
              }
            >
              <div className="description">
                <img src="/monitoring.png?v=2" alt="Data Collection" />
                <h4>{<FormattedMessage id="general.monitoring" defaultMessage="Data Collection" />}</h4>
                <SectionInfo section="monitoring" {...response} results={results} />
              </div>
            </div>
            <div
              className={
                results.assessment.complete ? "card complete" : "card"
              }
            >
              <div className="description">
                <img src="/assessment.png?v=2" alt="Assessment" />
                <h4>{<FormattedMessage id="general.assessment" defaultMessage="Assessment" />}</h4>
                <SectionInfo section="assessment" {...response} results={results} />
              </div>
            </div>
            <div
              className={
                results.controlRules.complete
                  ? "card complete"
                  : "card"
              }
            >
              <div className="description">
                <img src="/decisionrules.png?v=2" alt="Management Measures" />
                <h4>{<FormattedMessage id="general.controlRules" defaultMessage="Management Measures" />}</h4>
                <SectionInfo section="controlRules" {...response} results={results} />
              </div>
            </div>
          </div>
        </IntlProvider>
      </Dialog>
    );
  }
};

export default FisheryStatusModal;
